import { Flex, Form, Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import StatusComponent from 'components/status/status';
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';

// services

// styles

// models
import { Suspend } from '../models/suspend';
import { onCancelSystemSuspendRequest, onGetSystemSuspendRequestList } from '../services/suspendServices';
import NotificationService from 'core/services/notification';
import TableTotal from 'components/tableTotal/tableTotal';
import { dateFormat } from 'utils/dateFormat';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import FormElement from 'components/formElement/formElement';
import FileInput from 'components/fileInput/fileInput';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

interface SuspendListPageProps {}

const SystemSuspendListComponent: FunctionComponent<SuspendListPageProps> = () => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const location = useLocation();
	const [cancelForm] = Form.useForm();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requests, setRequests] = useState<Partial<Suspend>[]>([]);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
	const [isCancelling, setIsCancelling] = useState<boolean>(false);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

	const columns: TableProps<Partial<Suspend>>['columns'] = [
		{
			title: t('ACTIONS.REQUEST_CODE'),
			dataIndex: 'code',
			key: 'code',
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'date',
			render: (date) => dateFormat(date),
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('ACTIONS.TYPE'),
			dataIndex: 'type',
			key: 'type',
			render: (type, record) => (isRTL() ? record.translated_type?.ar : record.translated_type?.en),
		},
		{
			title: t('ACTIONS.CANCELLED_AT'),
			dataIndex: 'cancelled_at',
			key: 'cancelled_at',
			render: (date) => <span>{date ? dateFormat(date) : t('COMMON.N_A')}</span>,
		},
		{
			title: t('ACTIONS.SHAREHOLDER_NAME'),
			dataIndex: 'company_shareholder',
			key: 'company_shareholder',
			render: (company_shareholder, record) => (
				<span>
					{record.type == 'General'
						? t('ACTIONS.FOR_ALL')
						: isRTL()
						? company_shareholder?.name
						: company_shareholder?.name_en ?? company_shareholder?.name}
				</span>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => (
				<Flex gap={10} justify="center">
					<ListButton type="view" onClick={() => navigate(`system/${record.id}`)} />
					{record.can_canceled_by_user && (
						<ListButton
							type="reject"
							{...suspendedProps}
							onClick={() => {
								setSelectedRequestId(record.id!);
								setIsConfirmModalOpen(true);
							}}
						/>
					)}
				</Flex>
			),
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getSuspendRequestsList();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getSuspendRequestsList = async () => {
		try {
			setIsLoading(true);

			const response = await onGetSystemSuspendRequestList(getCurrentPage());

			setRequests(response.system_suspend_actions);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetSystemSuspendRequestList(getCurrentPage());
			setRequests(response.system_suspend_actions);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const confirmHandler = async () => {
		try {
			const cancelFormValues = await cancelForm.validateFields(['cancelled_reason', 'founder_document_1', 'founder_document_2']);

			setIsCancelling(true);

			const requestPayload = new FormData();
			requestPayload.append('cancelled_reason', cancelFormValues.cancelled_reason);
			requestPayload.append('founder_document_1', cancelFormValues.founder_document_1);
			requestPayload.append('founder_document_2', cancelFormValues.founder_document_2);

			await onCancelSystemSuspendRequest(selectedRequestId as number, requestPayload);

			setIsCancelling(false);
			setIsConfirmModalOpen(false);
			cancelForm.resetFields();
			getSuspendRequestsList();
		} catch (error: any) {
			if (error?.errorFields) return; // skip form validation errors

			setIsCancelling(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<Table
				columns={columns}
				dataSource={requests}
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				locale={{ emptyText: <NoData /> }}
			/>

			<ConfirmModalComponent
				open={isConfirmModalOpen}
				onDiscard={() => {
					setIsConfirmModalOpen(false);
					setSelectedRequestId(null);
					cancelForm.resetFields();
				}}
				onConfirm={confirmHandler}
				confirmText={t('COMMON.CONFIRM')}
				title={t('ACTIONS.CANCEL_REQUEST')}
				note={t('ACTIONS.CANCEL_REQUEST_NOTE')}
				noteType="red"
				isLoading={isCancelling}>
				<Form form={cancelForm} className="mt-3">
					<FormElement
						fullWidth
						name="cancelled_reason"
						type="text"
						label={t('ACTIONS.CANCEL_SUSPEND_REASON')}
						rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
					/>

					<Form.Item name="founder_document_1" style={{ maxWidth: 'unset' }} rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
						<FileInput
							name={t('ACTIONS.FIRST_YEAR_AUDITED_STATEMENT')}
							accept="pdf"
							onSelect={(file) => cancelForm.setFieldValue('founder_document_1', file)}
						/>
					</Form.Item>

					<Form.Item name="founder_document_2" style={{ maxWidth: 'unset' }} rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
						<FileInput
							name={t('ACTIONS.SECOND_YEAR_AUDITED_STATEMENT')}
							accept="pdf"
							onSelect={(file) => cancelForm.setFieldValue('founder_document_2', file)}
						/>
					</Form.Item>
				</Form>
			</ConfirmModalComponent>
		</>
	);
};

export default SystemSuspendListComponent;
